import React, { useCallback, useState } from 'react';
import { SalesMetricsFilters } from './SalesMetricsFilters';
import { TopPerformers } from './TopPerformers/TopPerformers';
import { useSalesMetricsFilterOptions } from 'services/DashboardApi/queries/useSalesMetricsFilters';
import { ErrorBar, LoadingBar } from 'ui/NetworkStatusBar';
import { ISalesMetricsFiltersAction, useSalesMetricsFiltersReducer } from './hooks/useSalesMetricsFiltersReducer';
import { useSalesMetrics } from 'services/DashboardApi/queries/useSalesMetrics';
import {
  ESalesMetricsGroupBy,
  IBookingsByDestinationRequest,
  ISalesMetricsFilters,
  ISalesMetricsFiltersRequest,
  ISalesMetricsRequest,
} from 'services/DashboardApi/types';
import { SalesMetricsData } from './SalesMetricsData';
import { validateMonthRange } from 'ui/MonthPicker/MonthRangePicker';
import { useBookingsByDestination } from 'services/DashboardApi/queries/useBookingsByDestination';
import { BookingsByDestinationData } from './BookingsByDestinationData';
import { dateToYearMonth } from '../helpers';
import { useResponsive } from 'containers/CRM/hooks/useResponsive';
import FluidButton from 'ui/FluidButton';
import { SalesMetricsFiltersDrawer } from './SalesMetricsFiltersDrawer';

interface ISalesMetricsProps {
  salesUserId: string;
  dateFrom: string;
  dateTo: string;
  filters: ISalesMetricsFilters;
  dispatchFilters: (action: ISalesMetricsFiltersAction) => void;
}

export const SalesMetrics: React.FC<ISalesMetricsProps> = ({ salesUserId, dateFrom, dateTo, filters, dispatchFilters }) => {
  const { isMobile } = useResponsive();

  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const handleCloseFiltersDrawer = useCallback(() => {
    setShowFiltersDrawer(false);
  }, []);

  const salesUserFilters: ISalesMetricsFiltersRequest = {
    salesUserUuid: salesUserId,
  };
  const {
    salesMetricsFilterOptions,
    isLoadingSalesMetricsFilterOptions,
    isErrorSalesMetricsFilterOptions,
  } = useSalesMetricsFilterOptions(salesUserFilters);

  const { year: startYear, month: startMonth } = dateToYearMonth(dateFrom);
  const { year: endYear, month: endMonth } = dateToYearMonth(dateTo);

  const salesMetricsRequest: ISalesMetricsRequest = {
    ...filters,
    startYear,
    startMonth,
    endYear,
    endMonth,
    salesUserUuid: salesUserId,
  };
  const { salesMetricsData, isLoadingSalesMetricsData, isErrorSalesMetricsData } = useSalesMetrics(salesMetricsRequest);
  const bookingsByDestinationRequest: IBookingsByDestinationRequest = {
    ...filters,
    startYear,
    startMonth,
    endYear,
    endMonth,
    salesUserUuid: salesUserId,
  };
  const {
    bookingsByDestinationData,
    isLoadingBookingsByDestinationData,
    isErrorBookingsByDestinationData,
  } = useBookingsByDestination(bookingsByDestinationRequest);

  if (isErrorSalesMetricsFilterOptions) {
    return (
      <div className="w-full flex justify-center items-center mt-5 mb-5">
        <ErrorBar message="Error loading sales metrics filters" />
      </div>
    );
  }

  const errorMessage = validateMonthRange({ monthFrom: dateFrom?.slice(0, 7), monthTo: dateTo?.slice(0, 7) }, 12); // max period in months allowed is 12
  const showError = errorMessage != null && errorMessage.length > 0;
  if (showError) {
    return (
      <div className="w-full flex justify-center items-center mt-5 mb-5">
        <ErrorBar message={errorMessage} showCta={false} />
      </div>
    );
  }

  if (isLoadingSalesMetricsFilterOptions || !salesMetricsFilterOptions) {
    return (
      <div className="w-full flex justify-center items-center mt-5 mb-5">
        <LoadingBar label="Loading" />
      </div>
    );
  }

  return (
    <div className="crm-dashboard-sales-metrics w-full px-[20px] py-[20px] crm-desktop:px-[30px] crm-desktop:py-[40px]">
      <p className="font-noe-display text-[26px] leading-33px text-black font-normal m-0 mb-[20px]">Sales Metrics</p>

      {isMobile ? (
        <div className="mb-[20px]">
          <FluidButton type="secondary" onClick={() => setShowFiltersDrawer(true)}>
            Show Filters
          </FluidButton>
        </div>
      ) : (
        <SalesMetricsFilters
          filterOptions={salesMetricsFilterOptions}
          filters={filters}
          dispatchFilters={dispatchFilters}
        />
      )}

      <div className="flex flex-col crm-desktop:flex-row gap-[20px] mt-[20px]">
        <div className="w-full crm-desktop:flex-[940]">
          <SalesMetricsData
            salesMetricsData={salesMetricsData}
            isLoadingSalesMetricsData={isLoadingSalesMetricsData}
            isErrorSalesMetricsData={isErrorSalesMetricsData}
          />
        </div>
        <div className="w-full crm-desktop:min-w-[265px] crm-desktop:flex-[260] border border-solid border-gray-20">
          <BookingsByDestinationData
            bookingsByDestinationData={bookingsByDestinationData}
            isLoadingBookingsByDestinationData={isLoadingBookingsByDestinationData}
            isErrorBookingsByDestinationData={isErrorBookingsByDestinationData}
          />
        </div>
      </div>
      {/* Notes */}
      <p
        className={`font-pt-sans text-gray-100 m-0 mt-10px ${isMobile ? 'text-[14px] leading-[normal]' : 'text-12px'}`}
      >
        <span className="underline">NOTE</span> All booking values are shown in USD. Bookings originally made in EUR are
        converted using a standard exchange rate.
      </p>

      <TopPerformers
        startYear={startYear}
        startMonth={startMonth}
        endYear={endYear}
        endMonth={endMonth}
        dateType={filters.dateType ?? ESalesMetricsGroupBy.CREATION_DATE}
        status={filters.status.length > 0 ? filters.status : undefined}
        companyCountry={filters.companyCountry.length > 0 ? filters.companyCountry : undefined}
        company={filters.company.length > 0 ? filters.company : undefined}
        destination={filters.destination.length > 0 ? filters.destination : undefined}
        hotel={filters.hotel.length > 0 ? filters.hotel : undefined}
        salesUserUuid={salesUserId}
      />

      <SalesMetricsFiltersDrawer isOpen={showFiltersDrawer} onClose={handleCloseFiltersDrawer}>
        <SalesMetricsFilters
          filterOptions={salesMetricsFilterOptions}
          filters={filters}
          dispatchFilters={dispatchFilters}
          onApplyFilters={handleCloseFiltersDrawer}
        />
      </SalesMetricsFiltersDrawer>
    </div>
  );
};
